import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, Link } from 'react-router-dom';


import Stack from '@mui/joy/Stack';
import Card from '@mui/joy/Card';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Switch from '@mui/joy/Switch';

import Table from '@mui/joy/Table';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Grid from '@mui/joy/Grid';

import useRoles from 'hooks/useRoles';
import useUser from 'hooks/useUser';
import useIdentity from 'hooks/useIdentity';
import DateUI from 'components/Date';
import IdentityAvatar from 'components/IdentityAvatar';
import LockedFeature from 'components/LockedFeature';
import { UserProps } from 'stores/user_store';
import { IdentityProps } from 'stores/identity_store';
import { PaymentProps, RefundPaymentType } from 'stores/payment_store';
import { RoleProps } from 'stores/role_store';
import usePayment from 'hooks/usePayment';

import useUserIdentity from 'hooks/useUserIdentity';
import useWorkKey from 'hooks/useWorkKey';

import SupportCreditLog from 'components/SupportCreditLog';
import UserInfo from 'components/UserInfo';
import { SupportOptions } from 'components/IdentityPage';
import { Success, Error } from 'components/FancyToast';
import SupportStripeSubscriptionsList from 'components/SupportStripeSubscriptionsList';

import { getIcon, pluralize } from 'utils/misc';
import { capitalize } from 'utils/misc';
import RoleStack from './RoleStack';

const ManagerPaymentInvoiceOptions = observer(({ payment }: { payment: PaymentProps }) => {
    const { supportRefundPayment } = usePayment();
    const [ourPayment, setOurPayment] = useState(payment);
    const workKey = useWorkKey();
    const { hasRole } = useRoles();
    const [invoice_json, setInvoiceJson] = useState(ourPayment?.invoice_json);
    const payment_intent = invoice_json?.payment_intent;
    const payment_status = invoice_json?.status;

    async function doRefund() {
        if(!workKey) {
            Error("No identity loaded.");
            return;
        }
        return supportRefundPayment({
            identity: workKey,
            payment_uuid: payment.key,
            payment_intent_id: payment_intent
        } as RefundPaymentType).then((data: PaymentProps) => {
            console.log("Setting our Payment", data);
            setOurPayment(data);
            setInvoiceJson(data.invoice_json);
            Success("Great. Done!");
        }).catch(err => {
            Error("Unable to issue this refund");
            console.error('refund error', err);
        })
    }

    if(!hasRole('support')) {
        return <Typography level="body-xs">not yet</Typography>;
    }

    if (payment_status != 'paid') {
        return <Chip>{payment_status}</Chip>;
    }

    if (ourPayment.refunded) {
        return <Typography level="body-xs">Refunded</Typography>;
    }

    return (
        <LockedFeature copy="Refund Options">
            <Stack spacing={1}>
                <Button
                    onClick={() => { doRefund() }}
                    variant="plain"
                    color="danger"
                    size="sm">Issue Refund</Button>
            </Stack>
        </LockedFeature>
    );

});



const SupportSubscriptionsList = observer(({ user, onPaymentsSync }: { user: UserProps, onPaymentsSync?: (payments: PaymentProps[]) => void }) => {
    if(user.subscription_service == 'stripe') {
        return <SupportStripeSubscriptionsList user={user} onPaymentsSync={onPaymentsSync} />;
    }
});



const SupportPaymentsList = observer(({ userUuid, syncedPayments }: { userUuid: string, syncedPayments?: PaymentProps[] }) => {
    const { supportFetchPayments } = usePayment();
    const { currentIdentity } = useUserIdentity();
    const [payments, setPayments] = useState([] as PaymentProps[]);
    const [didFetch, setDidFetch] = useState(false);

    // If syncedPayments are provided, merge them into the payments list
    useEffect(() => {
        if (syncedPayments && syncedPayments.length > 0) {
            // Merge synced payments, avoiding duplicates by key
            const existingKeys = new Set(payments.map(p => p.key));
            const newPayments = syncedPayments.filter(p => !existingKeys.has(p.key));
            if (newPayments.length > 0) {
                setPayments([...newPayments, ...payments]);
                Success(`Added ${newPayments.length} synced payments to the list`);
            }
        }
    }, [syncedPayments]);

    useEffect(() => {
        if (didFetch) {
            return;
        }

        supportFetchPayments(currentIdentity.uuid, userUuid)
            .then((result) => {
                const items = result.items;
                setPayments([...payments, ...items]);
            })
            .catch(() => {
                setPayments([]);
            })
            .finally(() => {
                setDidFetch(true);
            });
    }, [supportFetchPayments, userUuid, didFetch]);

    return (
        <Card>
            <Stack>
                <Typography level="title-md">Payments</Typography>
                <Table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Product</th>
                            <th>Options</th>
                        </tr>
                    </thead>

                    {payments.map((payment: any) => {
                        return (
                            <tr key={payment.key}>

                                <td>
                                    <DateUI dateString={payment.date_created} />
                                </td>

                                <td>
                                    <Typography level="body-sm">
                                        ${payment.amount} ({payment.total})
                                    </Typography>
                                </td>

                                <td>
                                    <Stack>
                                        {payment.minutes_included > 0 &&
                                            <Typography>{payment.minutes_included} minutes</Typography>}
                                        {payment.tasks_included > 0 &&
                                            <Typography>{payment.tasks_included} tasks included</Typography>}
                                        {payment.tasks_used > 0 &&
                                            <Typography level="body-xs">{payment.tasks_used} used</Typography>}
                                    </Stack>
                                </td>

                                <td>
                                    { payment.invoice_json ?
                                        <ManagerPaymentInvoiceOptions payment={payment} />
                                        :
                                        <Typography level="body-xs">No Invoice</Typography> }
                                </td>
                            </tr>
                        );
                    })}
                </Table>
            </Stack>
        </Card>
    );
});


const SudoUserBlock = observer(({ user }: { user: UserProps }) => {
    const { rolesNames, sudoList, sudoGrant, sudoRevoke } = useRoles();
    const {
        sudoSetAllowedIdentities,
        sudoUpdateICStatus,
        sudoUpdateIsBeta,
        sudoUpdateIsDisabled
    } = useUser();
    const workKey = useWorkKey();
    const { currentIdentity } = useUserIdentity();
    const [roles, setRoles] = useState([] as RoleProps[]);

    const [userIsManager, setUserIsManager] = useState(false);
    const [userIsSupport, setUserIsSupport] = useState(false);
    const [userIsTrustedTester, setUserIsTrustedTester] = useState(false);
    const [allowedIdentities, setAllowedIdentities] = useState(user.allowed_identities);
    const [savingIds, setSavingIds] = useState(false);
    const RoleIcon = getIcon('LockPerson');

    function updateRoles(r: RoleProps[]) {
        setRoles(r)
        setUserIsManager(r.filter(rr => rr.role == 'manager').length > 0);
        setUserIsSupport(r.filter(rr => rr.role == 'support').length > 0);
        setUserIsTrustedTester(r.filter(rr => rr.role == 'trusted-tester').length > 0);
    }

    useEffect(() => {
        if(!rolesNames.includes('sudo')) {
            return;
        }
        sudoList(user.key).then(r => {
            updateRoles(r);
        });
    }, [sudoList, user.key]);


    const doGrant = (role: string) => {
        sudoGrant(user.key, role).then(updateRoles);
    };

    const doRevoke = (role: string) => {
        sudoRevoke(user.key, role).then(updateRoles);
    };

    const updateICStatus = (is_ic: boolean) => {
        if(!workKey) return;
        sudoUpdateICStatus(workKey, user.uuid, is_ic).then(() => {
            Success('Great. IC status updated! (Please refresh)');
        });
    };


    const updateIsBeta = (is_beta: boolean) => {
        if(!workKey) return;
        sudoUpdateIsBeta(workKey, user.uuid, is_beta).then(() => {
            Success('Great. is_beta updated! (Please refresh)');
        });
    };

    const updateIsDisabled = (is_disabled: boolean) => {
        if(!workKey) return;
        sudoUpdateIsDisabled(workKey, user.uuid, is_disabled).then(() => {
            Success('Great. is_disabled updated! (Please refresh)');
        });
    };

    const saveAllowedIdentities = (n: number) => {
        setSavingIds(true);
        sudoSetAllowedIdentities(user.key, n, currentIdentity.key).then(() => {
            setSavingIds(false);
            setAllowedIdentities(n);
            Success(`Great. Their identity limit is ${n}! (you may have to refresh)`);
        });
        console.log('saveAllowedIdentities', n);
    };

    // rolesNames is for the current user
    if(!rolesNames.includes('sudo'))
        return null;

    return (
        <Card color="danger">

            <Stack spacing={2}>
                <Typography level="title-md">Sudo Options</Typography>

                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    <Typography level="body-sm">{pluralize(roles.length, "Role")}</Typography>
                    {roles.map(r => <Chip startDecorator={<RoleIcon />}>{r.role}</Chip>)}
                </Stack>

                <Stack direction={{ xs: 'column', md: "row"}} spacing={1}>
                    <LockedFeature copy="Role Editor">
                        <Stack spacing={1}>

                            <Typography level="title-sm">Manager Role</Typography>
                            {userIsManager ?
                                <Button onClick={() => doRevoke('manager')}
                                    variant="plain"
                                    color="success"
                                    size="sm">Remove Manager Role</Button>
                                :
                                <Button onClick={() => doGrant('manager')}
                                    variant="plain"
                                    color="danger"
                                    size="sm">Make this user a Manager</Button>}
                        </Stack>
                        <Stack spacing={1}>
                            <Typography level="title-sm">Support Role</Typography>
                            {userIsSupport ?
                                <Button onClick={() => doRevoke('support')}
                                    variant="plain"
                                    color="success"
                                    size="sm">Remove Support Role</Button>
                                :
                                <Button onClick={() => doGrant('support')}
                                    variant="plain"
                                    color="danger"
                                    size="sm">Add to Support team</Button>}
                                    </Stack>

                        <Stack spacing={1}>
                            <Typography level="title-sm">Trusted Tester Status</Typography>
                            {userIsTrustedTester ?
                                <Button onClick={() => doRevoke('trusted-tester')}
                                    variant="plain"
                                    color="success"
                                    size="sm">Remove Trusted Tester Role</Button>
                                :
                                <Button onClick={() => doGrant('trusted-tester')}
                                    variant="plain"
                                    color="danger"
                                    size="sm">Add to Trusted Tester team</Button>}
                        </Stack>

                    </LockedFeature>


                    <LockedFeature copy="Allowed Identities">
                        <Stack spacing={1}>
                            <Typography level="title-sm">
                                How many identities can this person have?
                            </Typography>
                            <Typography level="body-xs">
                                Currently allowed {user.allowed_identities == 1 ? `${user.allowed_identities} Identity` : `${user.allowed_identities} Identities`}
                            </Typography>
                            <Select
                                onChange={(_e, value) => value && setAllowedIdentities(value)}
                                value={allowedIdentities}>
                                {[1, 2, 3].map((n: number) => {
                                    return (
                                        <Option value={n}>{n}</Option>
                                    );
                                })}
                            </Select>
                            { allowedIdentities != user.allowed_identities && !savingIds &&
                                <Button
                                    onClick={() => saveAllowedIdentities(allowedIdentities)}
                                    variant="plain"
                                    color="success"
                                    size="sm">Save</Button>
                            }
                            { savingIds &&
                                <Typography level="body-xs">Saving...</Typography>
                            }
                        </Stack>
                    </LockedFeature>

                    <LockedFeature copy="IC Status">
                        <Stack spacing={1} m={2}>
                            <Typography level="title-sm">Update IC Status</Typography>
                            <Typography level="body-xs">
                                { user.is_ic ? 'This user is currently an IC' : 'This user is an employee' }
                            </Typography>

                            <Typography endDecorator={user.is_ic ?
                                <Chip color='success'>Yes</Chip> :
                                <Chip color='danger'>No</Chip> }
                            >Currently IC</Typography>
                            <Switch
                                checked={user.is_ic}
                                onChange={(event) => updateICStatus(event.target.checked)}
                            />
                            </Stack>


                    </LockedFeature>


                            <LockedFeature copy="Is Assistant? (is_beta)">
                        <Stack spacing={1} m={2}>
                            <Typography level="title-sm">Update is_beta Status</Typography>
                            <Typography level="body-xs">
                                { user.is_beta ? 'This user is not currently an assistant' : 'This user is an assistant' }
                            </Typography>

                            <Typography endDecorator={user.is_beta ?
                                <Chip color='success'>Yes</Chip> :
                                <Chip color='danger'>No</Chip> }
                            >Currently is_beta</Typography>
                            <Switch
                                checked={user.is_beta}
                                onChange={(event) => updateIsBeta(event.target.checked)}
                            />
                            </Stack>
                            </LockedFeature>

                            <LockedFeature copy="Disable This User">
                        <Stack spacing={1} m={2}>
                            <Typography level="title-sm">Update is_beta Status</Typography>
                            <Typography level="body-xs">
                                { user.is_disabled ? 'This user is disabled' : 'This user it not disabled' }
                            </Typography>

                            <Typography endDecorator={user.is_disabled ?
                                <Chip color='success'>Yes</Chip> :
                                <Chip color='danger'>No</Chip> }
                            >Currently_beta</Typography>
                            <Switch
                                checked={user.is_disabled}
                                onChange={(event) => updateIsDisabled(event.target.checked)}
                            />
                            </Stack>
                            </LockedFeature>


                </Stack>
            </Stack>

        </Card>
    );

});

const ManagerUserInfo = observer(({ user }: { user: UserProps }) => {
    const { hasRole } = useRoles();
    if (!hasRole('manager'))
        return null;
    if(!(user.header_ip_addr || user.user_agent))
        return null;

    return (
        <Card color="warning">
        <Stack spacing={1}>
            <Typography level="title-md">Manager Info</Typography>
            {user.user_agent &&
                <Stack spacing={1}>
                    <Typography level="body-xs">User Agent</Typography>
                    <Typography level="body-sm">{user.user_agent}</Typography>
                </Stack>
            }
            { user.header_ip_addr &&
                <Stack spacing={1}>
                    <Stack>
                    <Typography level="body-xs">Header IP address</Typography>
                    <Typography level="body-sm">{user.header_ip_addr}</Typography>
                    </Stack>
                    { user.header_ip_country &&
                <Stack >
                    <Typography level="body-xs">Header IP Country</Typography>
                    <Typography level="body-sm">{user.header_ip_country}</Typography>
                </Stack>
                    }
                    { user.header_ip_city &&
                <Stack >
                    <Typography level="body-xs">Header IP City</Typography>
                    <Typography level="body-sm">{user.header_ip_city}</Typography>
                </Stack>
                    }
                    { user.header_ip_region &&
                <Stack >
                    <Typography level="body-xs">Header IP Region</Typography>
                    <Typography level="body-sm">{user.header_ip_region}</Typography>
                </Stack>
                    }
                    { user.header_ip_regioncode &&
                <Stack >
                    <Typography level="body-xs">Header IP Region code</Typography>
                    <Typography level="body-sm">{user.header_ip_regioncode}</Typography>
                </Stack>
                    }

            </Stack>
            }
        </Stack>
        </Card>
    );
});

const UserSubscriptionProduct = observer(({ user }: { user: UserProps }) => {
    const { hasRole } = useRoles();
    const workKey = useWorkKey();
    const [showProductForm, setShowProductForm] = useState(false);
    const [product, setProduct] = useState('');
    const [savedProduct, setSavedProduct] = useState(user.subscription_product);

    const { managerSetSubscriptionProduct } = useUser();
    function saveProduct() {
        if (!workKey) return;
        managerSetSubscriptionProduct(workKey, user.uuid, product)
            .then(() => {
                setSavedProduct(product);
                Success("Great. Saved!");
                setShowProductForm(false);
            });
    }
    return (
        <Stack>
            <Typography
                level="body-xs">
                Subscription Product: {savedProduct || 'nothing'}
                {hasRole('manager') &&
                    <Button
                        onClick={() => setShowProductForm(!showProductForm)}
                        variant="plain"
                        size="sm">{showProductForm ? 'Hide' : 'Change'}</Button>
                }
            </Typography>
            {showProductForm &&

                <Stack direction="row" spacing={3}>

                    <Select onChange={(_, v) => v && setProduct(v.toString())}
                        defaultValue={user.subscription_product || ''}>
                        <Option value=''>None</Option>
                        <Option value='tasks'>Tasks</Option>
                        <Option value='dedicated'>Dedicated</Option>
                        <Option value='dayones'>Day Ones</Option>
                    </Select>
                    <Button onClick={() => saveProduct()} variant="plain" size="sm">Save</Button>
                </Stack>
            }
        </Stack>

    );

});

const UserSubscriptionService = observer(({ user }: { user: UserProps }) => {
    const { hasRole } = useRoles();
    const workKey = useWorkKey();
    const [showServiceForm, setShowServiceForm] = useState(false);
    const [service, setService] = useState('');
    const [savedService, setSavedService] = useState(user.subscription_service);



    const { managerSetSubscriptionService } = useUser();
    function saveService() {
        if (!workKey) return;
        managerSetSubscriptionService(workKey, user.uuid, service)
            .then(() => {
                setSavedService(service);
                Success("Great. Saved!");
                setShowServiceForm(false);
            });
    }
    return (
        <Stack>
            <Typography
                level="body-xs">
                Subscription Service: {savedService || 'nothing'}
                {hasRole('manager') &&
                    <Button
                        onClick={() => setShowServiceForm(!showServiceForm)}
                        variant="plain"
                        size="sm">{showServiceForm ? 'Hide' : 'Change'}</Button>
                }
            </Typography>
            {showServiceForm &&

                <Stack direction="row" spacing={3}>

                    <Select onChange={(_, v) => v && setService(v.toString())}
                        defaultValue={user.subscription_service || ''}>
                        <Option value=''>None</Option>
                        <Option value='stripe'>Stripe</Option>
                        <Option value='recurly'>Recurly</Option>
                    </Select>
                    <Button onClick={() => saveService()} variant="plain" size="sm">Save</Button>
                </Stack>
            }

        </Stack>

    );

});

const UserRecurlyCreditsIncluded = observer(({ user }: { user: UserProps }) => {
    const { hasRole } = useRoles();
    const workKey = useWorkKey();
    const [showMinutesForm, setShowMinutesForm] = useState(false);
    const [minutes, setMinutes] = useState(0);
    const [savedMinutes, setSavedMinutes] = useState(user.credit_included);

    const { managerSetRecurlyIncludedMinutes } = useUser();
    function saveMinutes() {
        if (!workKey) return;
        console.log(managerSetRecurlyIncludedMinutes);
        managerSetRecurlyIncludedMinutes(workKey, user.uuid, minutes)
            .then(() => {
                setSavedMinutes(minutes);
                Success("Great. Saved!");
                setShowMinutesForm(false);
            });
    }

    return (
        <Stack>
            <Typography
                level="body-xs">
                Subscription Minutes: {savedMinutes || 'nothing'}
                {hasRole('manager') &&
                    <Button
                        onClick={() => setShowMinutesForm(!showMinutesForm)}
                        variant="plain"
                        size="sm">{showMinutesForm ? 'Hide' : 'Change'}</Button>
                }
            </Typography>
            {showMinutesForm &&
                <Stack spacing={1}>
                    <Stack direction="row" spacing={3}>
                    <Input onChange={(e) => setMinutes(parseInt(e.target.value))} defaultValue={user.credit_included || 0} value={minutes} />
                        <Button onClick={() => saveMinutes()} variant="plain" size="sm">Save</Button>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                    { [5, 10, 15, 20, 25, 30].map((n) => {
                        return (
                            <Chip onClick={() => setMinutes(n * 60)}>{n} Hours</Chip>
                        );
                    })}
                    </Stack>
                </Stack>
            }
        </Stack>

    );

});



const UserSubscriptionCustomerId = observer(({ user }: { user: UserProps }) => {
    const { hasRole } = useRoles();
    const workKey = useWorkKey();
    const [showCustomerIdForm, setShowCustomerIdForm] = useState(false);
    const [customerId, setCustomerId] = useState('');
    const [savedCustomerId, setSavedCustomerId] = useState(
        user.subscription_service == 'stripe' ? user.stripe_customer_id : user.recurly_account_code
    );

    const { managerSetSubscriptionCustomerId } = useUser();
    function saveCustomerId() {
        if (!workKey) return;
        managerSetSubscriptionCustomerId(workKey, user.uuid, customerId)
            .then(() => {
                setSavedCustomerId(customerId);
                Success("Great. Saved!");
                setShowCustomerIdForm(false);
            });
    }
    return (
        <Stack>
            <Typography
                level="body-xs">
                {capitalize(user.subscription_service)} Customer Id/Code: {savedCustomerId || 'nothing'}
                {hasRole('manager') &&
                    <Button
                        onClick={() => setShowCustomerIdForm(!showCustomerIdForm)}
                        variant="plain"
                        size="sm">{showCustomerIdForm ? 'Hide' : 'Change'}</Button>
                }
            </Typography>
            {showCustomerIdForm &&

                <Stack direction="row" spacing={3}>

                <Input onChange={(e) => setCustomerId(e.target.value)}
                                                       value={customerId || ''}
                />

                    <Button onClick={() => saveCustomerId()} variant="plain" size="sm">Save</Button>
                </Stack>
            }
        </Stack>

    );

});


const UserBlock = observer(({ user }: { user: UserProps }) => {
    const recurly_subdomain = import.meta.env.PROD ? 'fancyhands' : 'fancyhands-test';
    const stripe_path = import.meta.env.PROD ? 'https://dashboard.stripe.com/customers' : 'https://dashboard.stripe.com/test/customers';
    const [syncedPayments, setSyncedPayments] = useState<PaymentProps[]>([]);

    function handlePaymentsSync(payments: PaymentProps[]) {
        setSyncedPayments(payments);
    }

    return (
        <Stack spacing={1}>
            <Card color="primary"  >

                <Stack>
                    <Typography
                        level="body-xs">
                        Date Created: <DateUI dateString={user.date_created} />
                    </Typography>

                    <UserSubscriptionService user={user} />
                    <UserSubscriptionCustomerId user={user} />
                    <UserSubscriptionProduct user={user} />

                    { user.subscription_service == 'recurly' && user.subscription_product == 'dedicated' &&
                        <UserRecurlyCreditsIncluded user={user} />
                    }

                    {user.recurly_account_code &&
                        <RoleStack role='support'>
                            <Card>
                                <Typography level="body-md">To refund, upgrade, or cancel this account. Head to recurly.</Typography>
                                <Link target="_blank" to={`https://${recurly_subdomain}.recurly.com/accounts/${user.recurly_account_code}`}><Button>View Recurly Account</Button></Link>
                            </Card>
                        </RoleStack>
                    }

                    <RoleStack role="manager">
                        <Stack direction="row" spacing={1}>
                            {user.stripe_customer_id &&
                                <Link target="_blank" to={`${stripe_path}/${user.stripe_customer_id}`}>View Stripe Account</Link>}
                        </Stack>
                    </RoleStack>

                </Stack>


                <SupportSubscriptionsList user={user} onPaymentsSync={handlePaymentsSync} />
                <SupportPaymentsList userUuid={user.key} syncedPayments={syncedPayments} />
                <ManagerUserInfo user={user} />

                <RoleStack role="sudo">

                <SudoUserBlock  user={user} />
                </RoleStack>
                { user.subscription_product != 'tasks' &&
                    <SupportCreditLog userUuid={user.key} />
                }
            </Card>
        </Stack>
    );
});



const ManagerIdentityCreate = observer(({user}:{user: UserProps}) => {
    const { rolesNames } = useRoles();
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const [createdIdentity, setCreatedIdentity] = useState<IdentityProps | null>(null);
    const workKey = useWorkKey();

    const { managerCreateIdentity } = useIdentity();

    function onSaveClick() {
        if(!workKey) return;
        const user_uuid = user.uuid || user.key;
        managerCreateIdentity(workKey, user_uuid, name)
            .then((id) => {
                setCreatedIdentity(id);
                setDone(true);
                Success("Great! Saved!");
            })
            .catch(() => {
                Error("Unable to save this!");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    if(!rolesNames.includes('manager'))
        return null;

    if (done && createdIdentity) {
        return <IdentityAvatar
            size="sm"
            identityKey={createdIdentity.uuid} />
    }
    return (
        <Stack direction="row">
        <LockedFeature
            color="warning"
            copy="Manually Create Identity"
        >
            <Stack m={1}>
                <Typography level="title-md">
                    Create Identity for a Dedicated user
                </Typography>

                <Stack m={2} spacing={1}>
                <FormControl>
                    <FormLabel>Name</FormLabel>
                        <Input
                            sx={{width: '100%'}}
                            onChange={(e) => { setName(e.target.value); }}
                            value={name}
                            name="anything_but"
                            type="anything_but"
                            placeholder="First Last"
                        />
                </FormControl>
                        <Stack flexGrow={1} my={2} >
                            <Button
                                disabled={saving}
                                variant="solid"
                                color="primary"
                                onClick={onSaveClick}>
                                Save
                            </Button>
                </Stack>

                </Stack>


            </Stack>

        </LockedFeature>
        </Stack>

    );
});

const SupportUser = observer(() => {
    const { userUuid = "" } = useParams<{ userUuid: string }>();
    const { supportFetchIdentityForUser } = useIdentity();
    const { supportUserFetch } = useUser();
    const { rolesNames } = useRoles();
    const workKey = useWorkKey();

    const [didFetch, setDidFetch] = useState(false);
    const [didFetchUser, setDidFetchUser] = useState(false);
    const [fetchingIdentity, setFetchingIdentity] = useState(false);
    const [identityKey, setIdentityKey] = useState('');
    const [user, setUser] = useState<UserProps | null>(null);

    useEffect(() => {
        if(!workKey) {
            return;
        }
        supportUserFetch(workKey, userUuid).then((result: UserProps) => {
            if (!result) {
                return;
            }
            setUser(result);
        }).catch((err) => {
            console.error('no user caught:' + err);
        }).finally(() => {
            setDidFetchUser(true);
        });
    }, [supportUserFetch, userUuid]);

    const localFetchIdentity = useCallback(() => {
        if (didFetch) {
            return;
        }

        setFetchingIdentity(true);
        setIdentityKey('');

        supportFetchIdentityForUser(userUuid)
            .then((result: IdentityProps) => {
                setIdentityKey(result?.key);
                setFetchingIdentity(false);
            })
            .catch(() => {
                setFetchingIdentity(false);
            })
            .finally(() => {
                setDidFetch(true);
            });
    }, [supportFetchIdentityForUser, userUuid, didFetch, setDidFetch]);

    useEffect(() => {
        localFetchIdentity();
    }, [localFetchIdentity, userUuid]);


    if (!rolesNames.includes('support'))
        return null;

    if (!user) {
        if (didFetchUser) {
            return <div>no user</div>;
        }
        else {
            return <div>loading</div>;
        }
    }

    return (
        <Stack spacing={3} padding={2}>
            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <Stack spacing={2}>
                        <Typography level="title-md">User Info</Typography>
                        <UserInfo user={user} />
                    </Stack>
                </Grid>
                <Grid xs={12} md={6}>
                    {(!fetchingIdentity && identityKey) &&
                        <Stack spacing={2}>
                            <IdentityAvatar identityKey={identityKey} size="sm" />
                            <SupportOptions identityKey={identityKey} hideUserPageLink />
                        </Stack>
                    }
                    {(!fetchingIdentity && !identityKey && user) &&
                        <Stack spacing={2}>
                            <Typography level="body-xs">
                                This user does not have a Day Ones identity.
                            </Typography>

                            <Card>
                                <Stack spacing={1}>
                                    <Typography level="title-sm">Subscription Info</Typography>
                                    <Stack>
                                        <Typography level="body-xs">Service</Typography>
                                        <Typography level="body-md">{user.subscription_service || 'None'}</Typography>
                                    </Stack>
                                    <Stack>
                                        <Typography level="body-xs">Product</Typography>
                                        <Typography level="body-md">{user.subscription_product || 'None'}</Typography>
                                    </Stack>
                                    {user.next_billing_cycle_date &&
                                        <Stack>
                                            <Typography level="body-xs">Next Billing Cycle</Typography>
                                            <Typography level="body-md"><DateUI dateString={user.next_billing_cycle_date} /></Typography>
                                        </Stack>
                                    }
                                    {['dedicated', 'dayones'].includes(user.subscription_product) &&
                                        <>
                                            <Stack>
                                                <Typography level="body-xs">Time Included</Typography>
                                                <Typography level="body-md">{Math.round(user.credit_included / 60)} hours</Typography>
                                            </Stack>
                                            <Stack>
                                                <Typography level="body-xs">Time Remaining</Typography>
                                                <Typography level="body-md">{Math.round(user.current_credit / 60)} hours</Typography>
                                            </Stack>
                                        </>
                                    }
                                    {user.subscription_product === 'tasks' &&
                                        <Stack>
                                            <Typography level="body-xs">Tasks Remaining</Typography>
                                            <Typography level="body-md">{user.tasks_remaining}</Typography>
                                        </Stack>
                                    }
                                </Stack>
                            </Card>

                            {rolesNames.includes('manager') &&
                                <ManagerIdentityCreate user={user} />
                            }
                        </Stack>
                    }
                </Grid>
            </Grid>

            <Stack spacing={1}>
                <UserBlock user={user} />
            </Stack>
        </Stack>

    );

});



export default SupportUser;
